import { InputLabel, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Controller,
  FieldValues,
  Path,
  PathValue,
  useFormContext
} from 'react-hook-form';
import { CommonFieldProps } from '../form.types';

export type SelectFieldProps<T extends FieldValues> = Omit<
  CommonFieldProps<T>,
  'onChange'
> & {
  type: 'select';
  options: { label: string; value: string; disabled?: boolean }[] | string[];
  defaultValue: PathValue<T, Path<T>>;
  onChange?: (event: SelectChangeEvent<string>, value: string) => void;
};

export const SelectField = <T extends FieldValues>({
  name,
  label,
  options,
  defaultValue,
  onChange
}: SelectFieldProps<T>) => {
  const { control } = useFormContext();

  return (
    <>
      <InputLabel id={label.split(' ').join('-') + '-label'} sx={{ m: 1 }}>
        {label}
      </InputLabel>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => {
          return (
            <Select
              {...field}
              label={label}
              onChange={(event) => {
                const nextValue = event.target.value;
                field.onChange(nextValue);
                onChange?.(event, String(nextValue));
              }}
            >
              {options.map((option) => {
                // Handle both string[] and object[] formats
                const isString = typeof option === 'string';
                const value = isString ? option : option.value;
                const displayLabel = isString ? option : option.label;
                const disabled = isString ? false : option.disabled;

                return (
                  <MenuItem key={value} value={value} disabled={disabled}>
                    {displayLabel}
                  </MenuItem>
                );
              })}
            </Select>
          );
        }}
      />
    </>
  );
};
